import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from '../../firebaseConfig';
import { eventGameLabel } from '../constants/communityGames';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

const COLORS = {
  background: '#0b0d17',
  surface: '#141827',
  border: '#232a40',
  primary: '#FCD000',
  text: '#ffffff',
  textSecondary: '#a3adc2',
  textMuted: '#6e7688',
};

const FONTS = {
  sizes: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
  },
};

const SPACING = {
  sm: 10,
  md: 14,
  lg: 20,
  xl: 28,
  xxl: 40,
};

const BORDER_RADIUS = {
  sm: 6,
  md: 10,
  lg: 14,
};

const SPONSOR_LEVEL_OPTIONS = [
  { id: 'presenting', label: 'Presented by' },
  { id: 'featured', label: 'Featured sponsors' },
  { id: 'community', label: 'Community sponsors' },
  { id: 'prize', label: 'Giveaway partners' },
  { id: 'not_sure', label: 'Not sure yet' },
];

const SPONSOR_PATH_OPTIONS = [
  {
    id: 'advertise',
    title: 'Advertise',
    description:
      'Advertise on this event through CritCast. Your name and logo go on the event page and the ceremony.',
  },
  {
    id: 'support',
    title: 'Support',
    description:
      'Support the event through its organizer. You arrange it with the organizer directly, and your name and logo go on the event page and the ceremony.',
  },
];

function formatLevelLabel(levelInterest) {
  if (levelInterest === 'presenting') return 'Presented by';
  if (levelInterest === 'featured') return 'Featured sponsors';
  if (levelInterest === 'community') return 'Community sponsors';
  if (levelInterest === 'prize') return 'Giveaway partners';
  return 'Not sure yet';
}

function formatPathLabel(sponsorPath) {
  if (sponsorPath === 'advertise') return 'Advertise';
  if (sponsorPath === 'support') return 'Support';
  return 'Sponsor';
}

function formatEventDate(ms) {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_) {
    return null;
  }
}

export default function SponsorMarketplaceScreen({
  navigation,
  route,
  initialEvents,
  initialPledges,
  initialPledgeForm,
  initialLoading,
  initialUser,
}) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const routeEventParam = (() => {
    if (route?.params?.event) return String(route.params.event);
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.search) {
      try {
        const sp = new URLSearchParams(window.location.search);
        const ev = sp.get('event');
        if (ev) return String(ev);
      } catch (_) {}
    }
    return '';
  })();

  const scrollRef = useRef(null);
  const pledgeSectionRef = useRef(null);
  const containerYRef = useRef(null);
  const pledgeSectionYRef = useRef(null);
  const pendingScrollRef = useRef(Boolean(routeEventParam));

  const scrollToPledgeForm = () => {
    // Web (Claude, 11-04 browser check): the document scrolls, not this
    // ScrollView (its height grows with its content), so scrollTo on it is a
    // no-op. The form's own DOM node scrolls itself into view wherever the
    // scrolling happens. Until the form has mounted, wait for its onLayout.
    if (Platform.OS === 'web') {
      const node = pledgeSectionRef.current;
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        pendingScrollRef.current = false;
      } else {
        pendingScrollRef.current = true;
      }
      return;
    }
    if (!scrollRef.current || typeof scrollRef.current.scrollTo !== 'function') {
      pendingScrollRef.current = true;
      return;
    }
    const innerNode =
      typeof scrollRef.current.getInnerViewNode === 'function'
        ? scrollRef.current.getInnerViewNode()
        : null;
    if (
      innerNode &&
      pledgeSectionRef.current &&
      typeof pledgeSectionRef.current.measureLayout === 'function'
    ) {
      pledgeSectionRef.current.measureLayout(
        innerNode,
        (_x, y) => {
          scrollRef.current?.scrollTo?.({ y, animated: true });
        },
        () => {
          if (containerYRef.current != null && pledgeSectionYRef.current != null) {
            scrollRef.current?.scrollTo?.({
              y: containerYRef.current + pledgeSectionYRef.current,
              animated: true,
            });
          }
        }
      );
      pendingScrollRef.current = false;
    } else if (containerYRef.current != null && pledgeSectionYRef.current != null) {
      scrollRef.current.scrollTo({
        y: containerYRef.current + pledgeSectionYRef.current,
        animated: true,
      });
      pendingScrollRef.current = false;
    } else {
      pendingScrollRef.current = true;
    }
  };

  const [currentUser, setCurrentUser] = useState(() =>
    initialUser !== undefined ? initialUser : auth?.currentUser || null
  );
  const [events, setEvents] = useState(() =>
    Array.isArray(initialEvents) ? initialEvents : []
  );
  const [eventsLoading, setEventsLoading] = useState(() =>
    initialLoading !== undefined ? Boolean(initialLoading) : !Array.isArray(initialEvents)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGame, setSelectedGame] = useState('All');
  const [selectedEventId, setSelectedEventId] = useState(() => {
    if (routeEventParam) return routeEventParam;
    if (Array.isArray(initialEvents) && initialEvents.length > 0) {
      const openEv =
        initialEvents.find((e) => Number(e.levelsOpen?.slotsLeft ?? 12) > 0) ||
        initialEvents[0];
      return openEv.tournamentId || '';
    }
    return '';
  });

  const [pledgeForm, setPledgeForm] = useState(
    initialPledgeForm || {
      sponsorPath: '',
      levelInterest: 'not_sure',
      companyName: '',
      contactName: '',
      email: auth?.currentUser?.email || '',
      phone: '',
      message: '',
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [pledgeSubmitted, setPledgeSubmitted] = useState(false);
  const [pledgeError, setPledgeError] = useState('');

  const [pledges, setPledges] = useState(() =>
    Array.isArray(initialPledges) ? initialPledges : []
  );
  const [pledgesLoading, setPledgesLoading] = useState(false);
  const [pledgesError, setPledgesError] = useState('');

  useEffect(() => {
    if (typeof onAuthStateChanged !== 'function' || !auth) {
      setCurrentUser(auth?.currentUser || null);
      return undefined;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
      if (user?.email) {
        setPledgeForm((prev) => (prev.email ? prev : { ...prev, email: user.email }));
      }
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadEvents = async () => {
      setEventsLoading(true);
      try {
        const fn = httpsCallable(functions, 'listSponsorableEvents');
        const res = await fn({});
        if (cancelled) return;
        const list = Array.isArray(res?.data?.events) ? res.data.events : [];
        setEvents(list);
        if (routeEventParam && list.some((e) => e.tournamentId === routeEventParam)) {
          setSelectedEventId(routeEventParam);
          scrollToPledgeForm();
        } else if (list.length > 0) {
          setSelectedEventId((prev) => {
            if (prev && list.some((e) => e.tournamentId === prev)) return prev;
            const openEv =
              list.find((e) => Number(e.levelsOpen?.slotsLeft ?? 12) > 0) || list[0];
            return openEv.tournamentId;
          });
        }
      } catch (_) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [routeEventParam]);

  const loadMyPledges = async (userObj) => {
    const activeUser = userObj !== undefined ? userObj : currentUser || auth?.currentUser;
    if (!activeUser) {
      setPledges([]);
      setPledgesError('');
      return;
    }
    setPledgesLoading(true);
    setPledgesError('');
    try {
      const fn = httpsCallable(functions, 'listMySponsorPledges');
      const res = await fn({});
      const list = Array.isArray(res?.data?.pledges) ? res.data.pledges : [];
      setPledges(list);
    } catch (_) {
      setPledges([]);
      setPledgesError("We couldn't load your pledges. Try again.");
    } finally {
      setPledgesLoading(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(initialPledges)) return;
    if (currentUser || auth?.currentUser) {
      loadMyPledges(currentUser || auth?.currentUser);
    } else {
      setPledges([]);
      setPledgesError('');
    }
  }, [currentUser]);

  const games = React.useMemo(() => {
    const set = new Set();
    for (const ev of events) {
      const label = eventGameLabel(ev);
      if (label && label !== 'Game') set.add(label);
    }
    return ['All', ...Array.from(set)];
  }, [events]);

  const filteredEvents = React.useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    return events.filter((ev) => {
      const gameLabel = eventGameLabel(ev);
      if (selectedGame !== 'All' && gameLabel !== selectedGame) return false;
      if (!q) return true;
      const hay = [ev.name, ev.game, gameLabel, ev.organizerName, ev.city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [events, searchQuery, selectedGame]);

  const selectedEvent =
    events.find((ev) => ev.tournamentId === selectedEventId) ||
    events.find((ev) => Number(ev.levelsOpen?.slotsLeft ?? 12) > 0) ||
    events[0] ||
    null;

  const availableLevelOptions = React.useMemo(() => {
    const presentingTaken = selectedEvent?.levelsOpen?.presenting === false;
    return SPONSOR_LEVEL_OPTIONS.filter(
      (lvl) => !(lvl.id === 'presenting' && presentingTaken)
    );
  }, [selectedEvent]);

  const handleSelectEventForPledge = (tournamentId) => {
    const targetEv = events.find((e) => e.tournamentId === tournamentId);
    if (targetEv && Number(targetEv.levelsOpen?.slotsLeft ?? 12) <= 0) {
      return;
    }
    setSelectedEventId(tournamentId);
    setPledgeSubmitted(false);
    setPledgeError('');
    const presentingTaken = targetEv?.levelsOpen?.presenting === false;
    setPledgeForm((prev) => ({
      ...prev,
      sponsorPath: '',
      levelInterest:
        presentingTaken && prev.levelInterest === 'presenting'
          ? 'not_sure'
          : prev.levelInterest,
    }));
    scrollToPledgeForm();
  };

  const handleViewEventPage = (ev) => {
    if (!ev) return;
    if (ev.championshipSlug) {
      navigation?.navigate?.('Championship', { slug: ev.championshipSlug });
    } else {
      navigation?.navigate?.('EventDetailScreen', {
        eventId: ev.tournamentId,
        tournamentId: ev.tournamentId,
      });
    }
  };

  const handleSubmitPledge = async () => {
    setPledgeError('');
    if (!selectedEvent) {
      setPledgeError('Please select an event to sponsor.');
      return;
    }
    if (Number(selectedEvent.levelsOpen?.slotsLeft ?? 12) <= 0) {
      setPledgeError('All sponsor slots are filled.');
      return;
    }
    if (pledgeForm.sponsorPath !== 'advertise' && pledgeForm.sponsorPath !== 'support') {
      setPledgeError('Choose Advertise or Support.');
      return;
    }
    if (
      !pledgeForm.companyName.trim() ||
      !pledgeForm.contactName.trim() ||
      !pledgeForm.email.trim()
    ) {
      setPledgeError('Please fill in company name, contact name, and email.');
      return;
    }

    setSubmitting(true);
    try {
      const submitInquiry = httpsCallable(functions, 'submitAdvertiserInquiry');
      await submitInquiry({
        tournamentId: selectedEvent.tournamentId,
        sponsorPath: pledgeForm.sponsorPath,
        levelInterest:
          pledgeForm.levelInterest === 'not_sure' ? null : pledgeForm.levelInterest,
        companyName: pledgeForm.companyName.trim(),
        contactName: pledgeForm.contactName.trim(),
        email: pledgeForm.email.trim(),
        phone: pledgeForm.phone.trim(),
        message: pledgeForm.message.trim(),
      });
      setPledgeForm((prev) => ({ ...prev, sponsorPath: '' }));
      setPledgeSubmitted(true);
      if (currentUser || auth?.currentUser) {
        await loadMyPledges(currentUser || auth?.currentUser);
      }
    } catch (err) {
      setPledgeError(err?.message || 'Failed to submit pledge. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSignedIn = Boolean(currentUser || auth?.currentUser);

  return (
    <View style={styles.root}>
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >
        <Header navigation={navigation} />

        {/* Hero */}
        <View style={styles.hero}>
          <View style={styles.heroBadge}>
            <Ionicons name="ribbon-outline" size={14} color={COLORS.primary} />
            <Text style={styles.heroBadgeText}>EVENT SPONSORSHIPS ON CRITCAST</Text>
          </View>
          <Text style={[styles.heroTitle, isMobile && styles.heroTitleMobile]}>
            Sponsor an Event on CritCast
          </Text>
          <Text style={styles.heroSub}>
            Put your business on an upcoming event. Approved sponsors have their name and logo
            featured on the event page and the ceremony, organized by sponsorship level:
            Presented by, Featured sponsors, Community sponsors, and Giveaway partners.
          </Text>
          {/* Dennis 11-04: always one click to the general advertising inquiry. */}
          <TouchableOpacity
            style={styles.heroInquiry}
            accessibilityRole="link"
            accessibilityLabel="Send a general inquiry"
            onPress={() => navigation?.navigate?.('Advertiser')}
          >
            <Text style={styles.heroInquiryText}>
              Advertising across all of CritCast?{' '}
              <Text style={styles.heroInquiryLink}>Send a general inquiry.</Text>
            </Text>
          </TouchableOpacity>
        </View>

        <View
          style={styles.container}
          onLayout={(e) => {
            const y = e?.nativeEvent?.layout?.y;
            if (typeof y === 'number') {
              containerYRef.current = y;
              if (pendingScrollRef.current && pledgeSectionYRef.current != null) {
                scrollToPledgeForm();
              }
            }
          }}
        >
          {/* Search & Game Filter */}
          {events.length > 0 && (
            <View style={styles.filterBar}>
              <View style={styles.searchBox}>
                <Ionicons name="search-outline" size={18} color={COLORS.textMuted} />
                <TextInput
                  style={styles.searchInput}
                  accessibilityLabel="Search events"
                  placeholder="Search by event name, game, organizer, or city..."
                  placeholderTextColor={COLORS.textMuted}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>
              <View style={styles.gameFilterRow}>
                {games.map((g) => {
                  const active = selectedGame === g;
                  return (
                    <TouchableOpacity
                      key={g}
                      style={[styles.gameChip, active && styles.gameChipActive]}
                      accessibilityLabel={`Filter game ${g}`}
                      onPress={() => setSelectedGame(g)}
                    >
                      <Text style={[styles.gameChipText, active && styles.gameChipTextActive]}>
                        {g}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {/* Events List or Empty State */}
          {eventsLoading ? (
            <View style={styles.centerBox}>
              <ActivityIndicator size="small" color={COLORS.primary} />
            </View>
          ) : events.length === 0 ? (
            <View style={styles.emptyCard}>
              <Ionicons name="calendar-outline" size={32} color={COLORS.textMuted} />
              <Text style={styles.emptyTitle}>No events are taking sponsors right now.</Text>
            </View>
          ) : filteredEvents.length === 0 ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>No events match your search.</Text>
            </View>
          ) : (
            <View style={styles.eventsGrid}>
              {filteredEvents.map((ev) => {
                const isSelected = selectedEvent && selectedEvent.tournamentId === ev.tournamentId;
                const dateLabel = formatEventDate(ev.startAt);
                const gameLabel = ev.game ? eventGameLabel(ev) : '';
                const slotsLeft = Number(ev.levelsOpen?.slotsLeft ?? 12);
                const isFull = slotsLeft <= 0;
                const presentingTaken = ev.levelsOpen?.presenting === false && slotsLeft > 0;
                const slotCountLabel =
                  slotsLeft === 1
                    ? '1 sponsor slot open'
                    : `${slotsLeft} sponsor slots open`;

                return (
                  <View
                    key={ev.tournamentId}
                    style={[styles.eventCard, isSelected && styles.eventCardSelected]}
                  >
                    <View style={styles.eventCardHeader}>
                      <Text style={styles.eventCardTitle}>{ev.name}</Text>
                      {gameLabel ? (
                        <View style={styles.gameBadge}>
                          <Text style={styles.gameBadgeText}>{gameLabel}</Text>
                        </View>
                      ) : null}
                    </View>

                    <View style={styles.metaList}>
                      {dateLabel ? (
                        <Text style={styles.metaItem}>Date: {dateLabel}</Text>
                      ) : null}
                      {ev.organizerName ? (
                        <Text style={styles.metaItem}>Organized by {ev.organizerName}</Text>
                      ) : null}
                      {ev.city ? (
                        <Text style={styles.metaItem}>City: {ev.city}</Text>
                      ) : null}
                      {ev.capacity ? (
                        <Text style={styles.metaItem}>Up to {ev.capacity} players</Text>
                      ) : null}
                    </View>

                    <View style={styles.levelsOpenBox}>
                      {isFull ? (
                        <Text style={styles.levelsOpenTitle}>
                          All sponsor slots are filled.
                        </Text>
                      ) : (
                        <>
                          <Text style={styles.levelsOpenTitle}>
                            Open levels ({slotCountLabel}):
                          </Text>
                          <Text style={styles.levelsOpenText}>
                            {presentingTaken
                              ? 'Featured sponsors, Community sponsors, Giveaway partners (Presented by filled)'
                              : 'Presented by, Featured sponsors, Community sponsors, Giveaway partners'}
                          </Text>
                        </>
                      )}
                    </View>

                    <View style={styles.cardActions}>
                      <TouchableOpacity
                        style={styles.linkBtn}
                        accessibilityLabel={`View event page ${ev.name}`}
                        onPress={() => handleViewEventPage(ev)}
                      >
                        <Text style={styles.linkBtnText}>View event page</Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[
                          styles.pledgeSelectBtn,
                          isSelected && !isFull && styles.pledgeSelectBtnActive,
                          isFull && styles.pledgeSelectBtnDisabled,
                        ]}
                        accessibilityLabel={`Pledge to sponsor ${ev.name}`}
                        disabled={isFull}
                        onPress={() => handleSelectEventForPledge(ev.tournamentId)}
                      >
                        <Text
                          style={[
                            styles.pledgeSelectBtnText,
                            isSelected && !isFull && styles.pledgeSelectBtnTextActive,
                            isFull && styles.pledgeSelectBtnTextDisabled,
                          ]}
                        >
                          Pledge to sponsor
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* Pledge Form */}
          {selectedEvent && (
            <View
              ref={pledgeSectionRef}
              style={styles.pledgeSection}
              onLayout={(e) => {
                const y = e?.nativeEvent?.layout?.y;
                if (typeof y === 'number') {
                  pledgeSectionYRef.current = y;
                  if (pendingScrollRef.current) {
                    scrollToPledgeForm();
                  }
                }
              }}
            >
              <Text style={styles.sectionTitle}>
                Pledge to sponsor: {selectedEvent.name}
              </Text>
              {Number(selectedEvent.levelsOpen?.slotsLeft ?? 12) <= 0 ? (
                <View style={styles.formCard}>
                  <Text style={styles.levelsOpenTitle}>All sponsor slots are filled.</Text>
                </View>
              ) : (
                <>
                  <Text style={styles.sectionSub}>
                    Tell us how you would like to sponsor this event. Our team reviews every pledge
                    and follows up directly.
                  </Text>

                  {pledgeSubmitted ? (
                <View style={styles.confirmationBox}>
                  <Ionicons name="checkmark-circle" size={36} color={COLORS.primary} />
                  <Text style={styles.confirmationText}>
                    Thanks. Your pledge is in. We'll be in touch within 1-2 business days.
                  </Text>
                </View>
              ) : (
                <View style={styles.formCard}>
                  <Text style={styles.fieldLabel}>How do you want to sponsor? *</Text>
                  <View style={styles.pathGrid}>
                    {SPONSOR_PATH_OPTIONS.map((opt) => {
                      const active = pledgeForm.sponsorPath === opt.id;
                      return (
                        <TouchableOpacity
                          key={opt.id}
                          style={[styles.pathCard, active && styles.pathCardActive]}
                          accessibilityLabel={opt.title}
                          onPress={() =>
                            setPledgeForm((prev) => ({ ...prev, sponsorPath: opt.id }))
                          }
                        >
                          <Text style={[styles.pathTitle, active && styles.pathTitleActive]}>
                            {opt.title}
                          </Text>
                          <Text style={styles.pathDesc}>{opt.description}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  <Text style={styles.fieldLabel}>Level of interest</Text>
                  <View style={styles.levelOptionsRow}>
                    {availableLevelOptions.map((lvl) => {
                      const active = pledgeForm.levelInterest === lvl.id;
                      return (
                        <TouchableOpacity
                          key={lvl.id}
                          style={[styles.levelOptionBtn, active && styles.levelOptionBtnActive]}
                          accessibilityLabel={lvl.label}
                          onPress={() =>
                            setPledgeForm((prev) => ({ ...prev, levelInterest: lvl.id }))
                          }
                        >
                          <Text
                            style={[
                              styles.levelOptionText,
                              active && styles.levelOptionTextActive,
                            ]}
                          >
                            {lvl.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
                    <View style={styles.formField}>
                      <Text style={styles.fieldLabel}>Company Name *</Text>
                      <TextInput
                        style={styles.input}
                        accessibilityLabel="Company Name"
                        placeholder="e.g., Local Game Shop"
                        placeholderTextColor={COLORS.textMuted}
                        value={pledgeForm.companyName}
                        onChangeText={(v) =>
                          setPledgeForm((prev) => ({ ...prev, companyName: v }))
                        }
                      />
                    </View>
                    <View style={styles.formField}>
                      <Text style={styles.fieldLabel}>Contact Name *</Text>
                      <TextInput
                        style={styles.input}
                        accessibilityLabel="Contact Name"
                        placeholder="Your full name"
                        placeholderTextColor={COLORS.textMuted}
                        value={pledgeForm.contactName}
                        onChangeText={(v) =>
                          setPledgeForm((prev) => ({ ...prev, contactName: v }))
                        }
                      />
                    </View>
                  </View>

                  <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
                    <View style={styles.formField}>
                      <Text style={styles.fieldLabel}>Email *</Text>
                      <TextInput
                        style={styles.input}
                        accessibilityLabel="Email"
                        placeholder="you@company.com"
                        placeholderTextColor={COLORS.textMuted}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        value={pledgeForm.email}
                        onChangeText={(v) => setPledgeForm((prev) => ({ ...prev, email: v }))}
                      />
                    </View>
                    <View style={styles.formField}>
                      <Text style={styles.fieldLabel}>Phone (Optional)</Text>
                      <TextInput
                        style={styles.input}
                        accessibilityLabel="Phone"
                        placeholder="+1 (555) 000-0000"
                        placeholderTextColor={COLORS.textMuted}
                        value={pledgeForm.phone}
                        onChangeText={(v) => setPledgeForm((prev) => ({ ...prev, phone: v }))}
                      />
                    </View>
                  </View>

                  <View style={styles.formFieldFull}>
                    <Text style={styles.fieldLabel}>Message (Optional)</Text>
                    <TextInput
                      style={[styles.input, styles.textArea]}
                      accessibilityLabel="Message"
                      placeholder="Tell us about your brand, products, or reward ideas..."
                      placeholderTextColor={COLORS.textMuted}
                      multiline
                      numberOfLines={3}
                      value={pledgeForm.message}
                      onChangeText={(v) => setPledgeForm((prev) => ({ ...prev, message: v }))}
                    />
                  </View>

                  {pledgeError ? <Text style={styles.errorText}>{pledgeError}</Text> : null}

                  <TouchableOpacity
                    style={[styles.submitBtn, submitting && { opacity: 0.6 }]}
                    accessibilityLabel="Submit Pledge"
                    onPress={handleSubmitPledge}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <ActivityIndicator size="small" color={COLORS.background} />
                    ) : (
                      <Text style={styles.submitBtnText}>Submit Pledge</Text>
                    )}
                  </TouchableOpacity>
                </View>
                  )}
                </>
              )}
            </View>
          )}

          {/* Your Pledges */}
          <View style={styles.pledgesSection}>
            <Text style={styles.sectionTitle}>Your pledges</Text>
            {!isSignedIn ? (
              <View style={styles.signInBox}>
                <Text style={styles.signInText}>
                  Sign in before you pledge to track it here.
                </Text>
                <TouchableOpacity
                  style={styles.secondaryBtn}
                  accessibilityLabel="Sign in"
                  onPress={() => navigation?.navigate?.('Login')}
                >
                  <Text style={styles.secondaryBtnText}>Sign in</Text>
                </TouchableOpacity>
              </View>
            ) : pledgesLoading ? (
              <ActivityIndicator size="small" color={COLORS.primary} />
            ) : pledgesError ? (
              <Text style={styles.errorText}>{pledgesError}</Text>
            ) : pledges.length === 0 ? (
              <Text style={styles.emptyPledgesText}>
                You haven't pledged to sponsor an event yet.
              </Text>
            ) : (
              <View style={styles.pledgesList}>
                {pledges.map((p) => (
                  <View key={p.inquiryId} style={styles.pledgeRow}>
                    <View style={styles.pledgeRowMain}>
                      <Text style={styles.pledgeEventName}>{p.eventName}</Text>
                      <Text style={styles.pledgeMeta}>
                        {formatPathLabel(p.sponsorPath)} • {formatLevelLabel(p.levelInterest)}
                      </Text>
                    </View>
                    <View style={styles.statusPill}>
                      <Text style={styles.statusPillText}>{p.status}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        <Footer navigation={navigation} />
      </ScrollView>
      <NavBar navigation={navigation} activeRoute="Sponsor" />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  hero: {
    paddingVertical: SPACING.xxl,
    paddingHorizontal: SPACING.lg,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: COLORS.primary + '18',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
    marginBottom: SPACING.md,
  },
  heroBadgeText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '800',
    letterSpacing: 1.2,
  },
  heroTitle: {
    color: COLORS.text,
    fontSize: 34,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  heroTitleMobile: {
    fontSize: 26,
  },
  heroInquiry: {
    marginTop: 14,
    alignSelf: 'center',
  },
  heroInquiryText: {
    color: COLORS.textMuted,
    fontSize: 14,
    textAlign: 'center',
  },
  heroInquiryLink: {
    color: COLORS.primary,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  heroSub: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.md,
    textAlign: 'center',
    maxWidth: 680,
    lineHeight: 22,
  },
  container: {
    maxWidth: 1040,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.xl,
  },
  filterBar: {
    marginBottom: SPACING.lg,
    gap: SPACING.sm,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchInput: {
    flex: 1,
    color: COLORS.text,
    fontSize: FONTS.sizes.md,
    paddingVertical: 4,
  },
  gameFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  gameChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.surface,
  },
  gameChipActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary + '18',
  },
  gameChipText: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
  },
  gameChipTextActive: {
    color: COLORS.primary,
  },
  centerBox: {
    paddingVertical: SPACING.xl,
    alignItems: 'center',
  },
  emptyCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.xl,
    alignItems: 'center',
    gap: SPACING.sm,
    marginBottom: SPACING.xl,
  },
  emptyTitle: {
    color: COLORS.text,
    fontSize: FONTS.sizes.lg,
    fontWeight: '800',
    textAlign: 'center',
  },
  emptySub: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.sm,
    textAlign: 'center',
    maxWidth: 480,
  },
  eventsGrid: {
    gap: SPACING.md,
    marginBottom: SPACING.xl,
  },
  eventCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.lg,
    gap: SPACING.sm,
  },
  eventCardSelected: {
    borderColor: COLORS.primary,
  },
  eventCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  eventCardTitle: {
    color: COLORS.text,
    fontSize: FONTS.sizes.lg,
    fontWeight: '800',
  },
  gameBadge: {
    backgroundColor: COLORS.primary + '18',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: BORDER_RADIUS.sm,
  },
  gameBadgeText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
  },
  metaList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  metaItem: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.sm,
  },
  levelsOpenBox: {
    backgroundColor: COLORS.background,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.sm,
  },
  levelsOpenTitle: {
    color: COLORS.text,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
    marginBottom: 2,
  },
  levelsOpenText: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.xs,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: SPACING.sm,
    marginTop: 4,
  },
  linkBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  linkBtnText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.sm,
    fontWeight: '700',
  },
  pledgeSelectBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.primary,
  },
  pledgeSelectBtnActive: {
    backgroundColor: COLORS.primary,
  },
  pledgeSelectBtnDisabled: {
    borderColor: COLORS.border,
    opacity: 0.5,
  },
  pledgeSelectBtnText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.sm,
    fontWeight: '800',
  },
  pledgeSelectBtnTextActive: {
    color: COLORS.background,
  },
  pledgeSelectBtnTextDisabled: {
    color: COLORS.textMuted,
  },
  pledgeSection: {
    marginBottom: SPACING.xl,
  },
  sectionTitle: {
    color: COLORS.text,
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 6,
  },
  sectionSub: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.sm,
    marginBottom: SPACING.md,
  },
  formCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.lg,
  },
  fieldLabel: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  pathGrid: {
    gap: SPACING.sm,
    marginBottom: SPACING.md,
  },
  pathCard: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.md,
    backgroundColor: COLORS.background,
  },
  pathCardActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary + '14',
  },
  pathTitle: {
    color: COLORS.text,
    fontSize: FONTS.sizes.md,
    fontWeight: '800',
    marginBottom: 4,
  },
  pathTitleActive: {
    color: COLORS.primary,
  },
  pathDesc: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.sm,
    lineHeight: 19,
  },
  levelOptionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: SPACING.md,
  },
  levelOptionBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: BORDER_RADIUS.sm,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.background,
  },
  levelOptionBtnActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary + '18',
  },
  levelOptionText: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
  },
  levelOptionTextActive: {
    color: COLORS.primary,
  },
  formRow: {
    flexDirection: 'row',
    gap: SPACING.md,
    marginBottom: SPACING.md,
  },
  formRowMobile: {
    flexDirection: 'column',
  },
  formField: {
    flex: 1,
  },
  formFieldFull: {
    marginBottom: SPACING.md,
  },
  input: {
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    paddingVertical: 10,
    color: COLORS.text,
    fontSize: FONTS.sizes.md,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#EF4444',
    fontSize: FONTS.sizes.sm,
    marginBottom: SPACING.md,
  },
  submitBtn: {
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: BORDER_RADIUS.md,
    alignItems: 'center',
  },
  submitBtnText: {
    color: COLORS.background,
    fontSize: FONTS.sizes.md,
    fontWeight: '800',
  },
  confirmationBox: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.primary + '55',
    padding: SPACING.xl,
    alignItems: 'center',
    gap: SPACING.sm,
  },
  confirmationText: {
    color: COLORS.text,
    fontSize: FONTS.sizes.md,
    fontWeight: '700',
    textAlign: 'center',
  },
  pledgesSection: {
    marginTop: SPACING.md,
  },
  signInBox: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.lg,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  signInText: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.sm,
  },
  secondaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.primary,
  },
  secondaryBtnText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.sm,
    fontWeight: '700',
  },
  emptyPledgesText: {
    color: COLORS.textMuted,
    fontSize: FONTS.sizes.sm,
  },
  pledgesList: {
    gap: SPACING.sm,
  },
  pledgeRow: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: SPACING.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  pledgeRowMain: {
    flex: 1,
  },
  pledgeEventName: {
    color: COLORS.text,
    fontSize: FONTS.sizes.md,
    fontWeight: '700',
  },
  pledgeMeta: {
    color: COLORS.textSecondary,
    fontSize: FONTS.sizes.xs,
    marginTop: 2,
  },
  statusPill: {
    backgroundColor: COLORS.primary + '18',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  statusPillText: {
    color: COLORS.primary,
    fontSize: FONTS.sizes.xs,
    fontWeight: '700',
  },
});
